import { useCallback, useMemo, useSyncExternalStore } from "react"

import {
  appendAction,
  appendJiraAction,
  removeActionById,
  selectIssueActions,
  selectProjectActions
} from "./takenActions.selector"
import {
  getTakenActions,
  setTakenActions,
  subscribeTakenActions
} from "./takenActions.store"

import type { TakenAction } from "./TakenActions.types"

// Actions taken per issue (runtime/pr/ci/jira), persisted so the triage column
// and the drawer stay in sync. Demo-only in practice.
export const useTakenActions = (issueId?: string, projectId?: string) => {
  const byIssue = useSyncExternalStore(subscribeTakenActions, getTakenActions)
  const actions = selectIssueActions(byIssue, issueId)

  const add = useCallback(
    (action: TakenAction) => {
      if (issueId)
        setTakenActions((previous) =>
          appendAction(previous, issueId, { projectId, ...action })
        )
    },
    [issueId, projectId]
  )

  const addJira = useCallback(() => {
    if (issueId)
      setTakenActions((previous) =>
        appendJiraAction(previous, issueId, {
          projectId,
          createdAt: Date.now()
        })
      )
  }, [issueId, projectId])

  const remove = useCallback(
    (actionId: string) => {
      if (issueId)
        setTakenActions((previous) =>
          removeActionById(previous, issueId, actionId)
        )
    },
    [issueId]
  )

  return { actions, add, addJira, remove }
}

export const useProjectTakenActions = (projectId?: string) => {
  const byIssue = useSyncExternalStore(subscribeTakenActions, getTakenActions)

  return useMemo(
    () => selectProjectActions(byIssue, projectId),
    [byIssue, projectId]
  )
}
